/**
 * The filter state — the thing the URL is the store for (PLAN §6).
 *
 * Deliberately a plain, flat, JSON-shaped record: it has to survive a
 * round trip through a query string, and it is the same set of axes the
 * v0.2 solver takes as its "step 0 — hard filters" input, so keeping it
 * dumb data means the solver can consume it verbatim later.
 *
 * Two invariants everything else leans on:
 *  - **Normalized**: numbers clamped to their documented ranges, id lists
 *    sorted and deduped. `normalizeFilters` is the only way in.
 *  - **Defaults show the whole road.** `DEFAULT_FILTERS` filters nothing, so
 *    a first visit is the complete 81-stop road and an empty query string.
 */

import type { CharacterId, ThreadId } from "@schema/catalog.ts";

export interface FilterState {
  /**
   * Tier ceiling as a `TierDef.rank`: 1 = essentials only … 5 = everything.
   * Entries whose tier ranks above this are hidden.
   */
  tierMax: number;
  /** Quality floor on `Quality.score`, 0–100. 0 = no gate. */
  qualityMin: number;
  /**
   * Essentials bypass the quality floor — an essential-but-mediocre film is
   * still essential (PLAN §3, step 0). Default on; turning it off makes the
   * gate absolute.
   */
  essentialsIgnoreQuality: boolean;
  /** Selected character ids; an entry matches if it links ANY of them. Empty = no character filter. */
  characters: CharacterId[];
  /** Selected thread ids, same OR semantics. Empty = no thread filter. */
  threads: ThreadId[];
  /** Per-entry runtime ceiling in minutes; `null` = no cap. */
  maxMinutes: number | null;
  /** Include `kind === "season"` entries (the ~33 Disney+ seasons). */
  includeSeries: boolean;
  /** Cost entries at their shortest curated route (`solver.minMinutes`) instead of full runtime. */
  useAlternatives: boolean;
}

/** Lowest/highest `TierDef.rank` — essential … skippable. */
export const TIER_RANK_MIN = 1;
export const TIER_RANK_MAX = 5;

export const QUALITY_MIN = 0;
export const QUALITY_MAX = 100;

/** Runtime cap bounds. The longest single entry in the catalog is a ~12h season. */
export const RUNTIME_CAP_MIN = 15;
export const RUNTIME_CAP_MAX = 900;

/** Filters nothing: the whole road, full runtimes. Also the "Completionist" track. */
export const DEFAULT_FILTERS: FilterState = {
  tierMax: TIER_RANK_MAX,
  qualityMin: QUALITY_MIN,
  essentialsIgnoreQuality: true,
  characters: [],
  threads: [],
  maxMinutes: null,
  includeSeries: true,
  useAlternatives: false,
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.round(value)));

const canonicalIds = (ids: readonly string[]): string[] => [...new Set(ids)].sort();

/**
 * The single door into a `FilterState`. Clamps every number into range and
 * puts id lists in canonical (sorted, deduped) order, so two states that mean
 * the same thing are `filtersEqual` and encode to the same query string.
 */
export function normalizeFilters(state: FilterState): FilterState {
  return {
    tierMax: clamp(state.tierMax, TIER_RANK_MIN, TIER_RANK_MAX),
    qualityMin: clamp(state.qualityMin, QUALITY_MIN, QUALITY_MAX),
    essentialsIgnoreQuality: state.essentialsIgnoreQuality,
    characters: canonicalIds(state.characters),
    threads: canonicalIds(state.threads),
    maxMinutes:
      state.maxMinutes === null ? null : clamp(state.maxMinutes, RUNTIME_CAP_MIN, RUNTIME_CAP_MAX),
    includeSeries: state.includeSeries,
    useAlternatives: state.useAlternatives,
  };
}

const sameIds = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/** Structural equality over normalized states. */
export function filtersEqual(a: FilterState, b: FilterState): boolean {
  return (
    a.tierMax === b.tierMax &&
    a.qualityMin === b.qualityMin &&
    a.essentialsIgnoreQuality === b.essentialsIgnoreQuality &&
    a.maxMinutes === b.maxMinutes &&
    a.includeSeries === b.includeSeries &&
    a.useAlternatives === b.useAlternatives &&
    sameIds(a.characters, b.characters) &&
    sameIds(a.threads, b.threads)
  );
}
